import { Download } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import FileViewer from "react-file-viewer";
import { Button } from "../../../ds2/components";
import "./AssetViewer.css";
import { WazToolbar } from "../toolbar/WazToolbar";
import { TextFileViewer } from "./TextFileViewer";
import { useSelector } from "react-redux";
import { selectEditingCollection, selectLastViewedMessageId } from "../../../redux/features/appData";

export const AssetViewer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentCollection = useSelector(selectEditingCollection);
  const lastViewedMessageId = useSelector(selectLastViewedMessageId);

  // Get title and URL from URL search parameters
  const title = searchParams.get("title") || "Untitled Asset";
  const url = searchParams.get("url") || "";

  // Determine file type and get appropriate icon
  const getFileTypeInfo = () => {
    if (!url) return { icon: null, type: "FILE" };

    const fileExtension = url.split(".").pop()?.toLowerCase();
    if (!fileExtension) return { icon: null, type: "FILE" };

    switch (fileExtension) {
      case "doc":
      case "docx":
        return { icon: "📄", type: fileExtension.toUpperCase() };
      case "pdf":
        return { icon: "📋", type: "PDF" };
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "svg":
      case "webp":
      case "bmp":
      case "ico":
        return { icon: "🖼️", type: fileExtension.toUpperCase() };
      case "mp4":
      case "avi":
      case "mov":
      case "wmv":
      case "webm":
      case "mkv":
      case "flv":
        return { icon: "🎥", type: fileExtension.toUpperCase() };
      case "mp3":
      case "wav":
      case "flac":
      case "aac":
      case "ogg":
      case "m4a":
        return { icon: "🎵", type: "AUDIO" };
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
      case "bz2":
      case "xz":
        return { icon: "📦", type: fileExtension.toUpperCase() };
      case "md":
      case "markdown":
        return { icon: "📝", type: "MARKDOWN" };
      case "txt":
      case "text":
        return { icon: "📄", type: "TEXT" };
      case "rtf":
        return { icon: "📄", type: "RTF" };
      case "json":
        return { icon: "🔧", type: "JSON" };
      case "xml":
      case "html":
      case "htm":
      case "css":
      case "js":
      case "ts":
      case "jsx":
      case "tsx":
        return { icon: "💻", type: fileExtension.toUpperCase() };
      case "csv":
        return { icon: "📊", type: "CSV" };
      default:
        return { icon: "📁", type: fileExtension.toUpperCase() };
    }
  };

  const { icon, type } = getFileTypeInfo();

  // Determine if file is viewable in browser
  const isViewableInBrowser = () => {
    if (!url) return false;

    const fileExtension = url.split(".").pop()?.toLowerCase();
    if (!fileExtension) return false;

    // Images
    if (
      ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "ico"].includes(
        fileExtension
      )
    )
      return true;

    // Videos
    if (["mp4", "webm", "ogg"].includes(fileExtension)) return true;

    // Audio
    if (["mp3", "wav", "ogg", "m4a"].includes(fileExtension)) return true;

    // Text-based files
    if (
      [
        "txt",
        "md",
        "markdown",
        "json",
        "xml",
        "html",
        "htm",
        "css",
        "js",
        "ts",
        "jsx",
        "tsx",
        "csv",
      ].includes(fileExtension)
    )
      return true;

    // PDFs and Word docs (with FileViewer)
    if (["pdf", "docx"].includes(fileExtension)) return true;

    return false;
  };

  const handleDownload = () => {
    // Open the URL in a new tab for download
    window.open(url, "_blank");
  };

  const handleCancel = () => {
    if (searchParams.get("returnPath") === "/waz/pins") {
      navigate("/waz/pins");  
    } else {
      // Navigate back to chat
      navigate("/waz/chat/" + currentCollection?.id + (lastViewedMessageId ? `/${lastViewedMessageId}` : ""));
    }
  };

  // Render preview content based on file type
  const renderPreviewContent = () => {
    const fileExtension = url.split(".").pop()?.toLowerCase();
    console.log("[AssetViewer] Rendering preview for file:", {
      url,
      fileExtension,
      title,
    });

    // Images
    if (
      ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "ico"].includes(
        fileExtension || ""
      )
    ) {
      return (
        <div className="preview-content image-preview">
          <img src={url} alt={title} className="preview-image" />
        </div>
      );
    }

    // Videos
    if (["mp4", "webm", "ogg"].includes(fileExtension || "")) {
      return (
        <div className="preview-content video-preview">
          <video controls className="preview-video">
            <source src={url} type={`video/${fileExtension}`} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Audio
    if (["mp3", "wav", "ogg", "m4a"].includes(fileExtension || "")) {
      return (
        <div className="preview-content audio-preview">
          <audio controls className="preview-audio">
            <source src={url} type={`audio/${fileExtension}`} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    // PDFs and Word docs
    if (["pdf", "docx"].includes(fileExtension || "")) {
      return (
        <div className="preview-content document-preview">
          <FileViewer
            fileType={fileExtension || "pdf"}
            filePath={url}
            errorComponent={(error: any) => (
              <div className="preview-error">
                <p>Error loading document: {error}</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="preview-link">
                  Open document in new tab
                </a>
              </div>
            )}
            onError={(error: any) =>
              console.error("[AssetViewer] FileViewer error:", error)
            }
          />
        </div>
      );
    }

    // Text-based files
    if (
      [
        "txt",
        "md",
        "markdown",
        "json",
        "xml",
        "html",
        "htm",
        "css",
        "js",
        "ts",
        "jsx",
        "tsx",
        "csv",
      ].includes(fileExtension || "")
    ) {
      return (
        <div className="preview-content text-preview">
          <TextFileViewer url={url} fileType={fileExtension || "txt"} />
        </div>
      );
    }

    // Fallback for other files
    return (
      <div className="preview-content fallback-preview">
        <p>Preview not available for this file type.</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="preview-link">
          Open file in new tab
        </a>
      </div>
    );
  };

  return (
    <div className="asset-viewer">
      <WazToolbar />

      <div className="viewer-container">
        <div className="viewer-header">
          <div className="viewer-title-section">
            <div className="viewer-file-info">
              <span className="file-icon">{icon}</span>
              <span className="viewer-file-type-label">{type}</span>
              <h2 className="viewer-file-title">{title}</h2>
            </div>
          </div>
          <div className="viewer-actions">
            <Button
              variant="primary"
              onClick={handleDownload}
              leftIcon={<Download size={16} />}>
              Download
            </Button>
            <Button
              variant="secondary"
              onClick={handleCancel}>
              Done
            </Button>
            
            {/* <button
              className="action-button download-button"
              onClick={handleDownload}>
              <Download size={16} />
              Download
            </button>
            <button
              className="action-button cancel-button"
              onClick={handleCancel}>
              Done
            </button> */}
          </div>
        </div>

        <div className="viewer-content">
          <div className="main-content">{renderPreviewContent()}</div>
        </div>
      </div>
    </div>
  );
};
